import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Calendar as CalendarIcon, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import {
  Loader, EmptyState, Pill,
  NAVY, CHAMPAGNE, GOLD, IVORY, SLATE, GREEN_LEAF, RED_LEAF,
} from "@/components/altiora/PremiumUI";

const RANGES = [
  { key: "today", label: "Aujourd'hui" },
  { key: "week",  label: "Semaine" },
  { key: "month", label: "Mois" },
  { key: "year",  label: "Année" },
  { key: "all",   label: "Depuis inscription" },
];


/**
 * TrajectoryTimeline — courbe SVG de l'évolution du Score Global.
 *
 * Chaque point est un snapshot quotidien (persisté par le backend).
 * Cliquer sur un point révèle les raisons de la variation.
 */
export default function TrajectoryTimeline() {
  const [range, setRange] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/trajectory/timeline?range=${range}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ points: [], count: 0 }))
      .finally(() => setLoading(false));
  }, [range]);

  return (
    <div className="rounded-2xl bg-white p-6"
         style={{ border: `1px solid ${CHAMPAGNE}33`, boxShadow: "0 4px 16px -6px rgba(11,37,69,0.08)" }}
         data-testid="trajectory-timeline-root">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" style={{ color: CHAMPAGNE }} strokeWidth={1.8} />
            <h3 className="font-serif text-xl tracking-tight" style={{ color: NAVY }}>
              Évolution de votre trajectoire
            </h3>
          </div>
          <p className="mt-1 text-[12px]" style={{ color: SLATE }}>
            Chaque point : un snapshot quotidien. Survolez pour voir les raisons de la variation.
          </p>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className="text-[11px] px-2.5 py-1 rounded-full transition-all"
              style={{
                background: range === r.key ? NAVY : "transparent",
                color: range === r.key ? IVORY : NAVY,
                border: `1px solid ${range === r.key ? NAVY : CHAMPAGNE}55`,
              }}
              data-testid={`timeline-range-${r.key}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <Loader label="Chargement de la timeline…" />}

      {!loading && data && data.points.length < 2 && (
        <EmptyState
          icon={CalendarIcon}
          title="Pas encore assez de données."
          description="Ta courbe se construit chaque jour. Reviens dans quelques jours pour voir l'évolution."
          testid="timeline-empty"
        />
      )}

      {!loading && data && data.points.length >= 2 && (
        <TimelineChart points={data.points} onHover={setHover} />
      )}

      {hover && (
        <div className="mt-4 rounded-md p-3 text-[12px]"
             style={{ background: `${CHAMPAGNE}15`, border: `1px solid ${CHAMPAGNE}44` }}
             data-testid="timeline-hover-details">
          <div className="flex items-center justify-between mb-1">
            <span className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: SLATE }}>
              {hover.date}
            </span>
            <span className="font-serif tabular-nums text-lg" style={{ color: NAVY }}>
              {hover.global_score}<span className="text-[11px] opacity-60">/100</span>
            </span>
          </div>
          {hover.reasons?.length > 0 ? (
            <ul className="space-y-0.5" style={{ color: NAVY }}>
              {hover.reasons.map((r, i) => (<li key={i}>· {r}</li>))}
            </ul>
          ) : (
            <span style={{ color: SLATE }}>Journée stable — aucune variation majeure.</span>
          )}
        </div>
      )}
    </div>
  );
}


function TimelineChart({ points, onHover }) {
  const W = 800, H = 220, PAD_X = 40, PAD_Y = 30;
  const min = Math.max(0, Math.min(...points.map(p => p.global_score)) - 5);
  const max = Math.min(100, Math.max(...points.map(p => p.global_score)) + 5);
  const range = max - min || 1;
  const stepX = (W - PAD_X * 2) / Math.max(points.length - 1, 1);

  const coords = points.map((p, i) => ({
    x: PAD_X + i * stepX,
    y: PAD_Y + (H - PAD_Y * 2) * (1 - (p.global_score - min) / range),
    p,
  }));

  const path = coords.map((c, i) => `${i === 0 ? "M" : "L"} ${c.x} ${c.y}`).join(" ");
  const area = `${path} L ${coords[coords.length - 1].x} ${H - PAD_Y} L ${coords[0].x} ${H - PAD_Y} Z`;

  return (
    <div className="relative overflow-x-auto" data-testid="timeline-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 600 }}>
        {/* Gridlines */}
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <line key={f}
                x1={PAD_X} x2={W - PAD_X}
                y1={PAD_Y + (H - PAD_Y * 2) * f}
                y2={PAD_Y + (H - PAD_Y * 2) * f}
                stroke={`${SLATE}22`} strokeWidth="1" />
        ))}
        {/* Y labels */}
        {[max, Math.round((max + min) / 2), min].map((v, i) => (
          <text key={i} x={PAD_X - 8} y={PAD_Y + (H - PAD_Y * 2) * (i / 2) + 4}
                fontSize="10" textAnchor="end" fill={SLATE}
                style={{ fontFamily: "monospace" }}>{Math.round(v)}</text>
        ))}
        {/* Area gradient */}
        <defs>
          <linearGradient id="areaGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={GOLD} stopOpacity="0.4" />
            <stop offset="100%" stopColor={GOLD} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#areaGrad)" />
        <motion.path
          d={path} fill="none" stroke={NAVY} strokeWidth="2" strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1 }}
        />
        {coords.map((c, i) => (
          <g key={i}
             onMouseEnter={() => onHover(c.p)}
             onMouseLeave={() => onHover(null)}
             style={{ cursor: "pointer" }}
             data-testid={`timeline-point-${i}`}>
            <circle cx={c.x} cy={c.y} r={4} fill={NAVY} stroke="#FFFFFF" strokeWidth="2" />
            <circle cx={c.x} cy={c.y} r={12} fill="transparent" />
          </g>
        ))}
        {/* X labels — first/mid/last */}
        {[0, Math.floor(coords.length / 2), coords.length - 1].map((idx) => (
          <text key={idx} x={coords[idx].x} y={H - 5} fontSize="10" textAnchor="middle" fill={SLATE}
                style={{ fontFamily: "monospace" }}>
            {coords[idx].p.date.slice(5)}
          </text>
        ))}
      </svg>
    </div>
  );
}
